import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'

export interface RemoveDependentDialogProps {
  /**
   * Full name of the dependent to remove.
   */
  fullName: string
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean
  /**
   * Callback when the dialog should close.
   */
  onClose?: () => unknown
  /**
   * Callback when the delete button is pressed.
   */
  onDeletePress?: () => unknown
  /**
   * Callback when the remove button is pressed.
   */
  onRemovePress?: () => unknown
  /**
   * Whether the remove operation is pending.
   */
  isRemovePending?: boolean
  /**
   * Whether the delete operation is pending.
   */
  isDeletePending?: boolean
  /**
   * Test ID attribute.
   */
  testId?: string
  /**
   * Test ID attribute for the remove button.
   */
  removeTestId?: string
  /**
   * Test ID attribute for the delete button.
   */
  deleteTestId?: string
  /**
   * Test ID attribute for the cancel button.
   */
  cancelTestId?: string
}

/**
 * A dialog for removing a dependent from the job.
 * Provides an option for deleting, too.
 */
export function RemoveDependentDialog({
  isOpen,
  onClose,
  fullName,
  isRemovePending,
  isDeletePending,
  onDeletePress,
  onRemovePress,
  testId,
  removeTestId,
  deleteTestId,
  cancelTestId,
}: RemoveDependentDialogProps) {
  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isRemovePending && !isDeletePending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>Remove dependent?</ModalDialogTitle>
        <ModalDialogDescription>
          <p>
            Are you sure you want to remove{' '}
            <span className="text-strong">{fullName}</span> as a dependent?
          </p>
          <p>
            Removing them as a dependent means you won't be claiming them on
            your taxes for this job, but they will still be available to add in
            the future, unless you choose to{' '}
            <span className="text-strong">delete them permanently</span>.
          </p>
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Remove from this job"
            variant="default"
            onPress={onRemovePress}
            isPending={isRemovePending}
            isDisabled={isDeletePending}
            testId={removeTestId}
          />
          <Button
            label="Delete permanently"
            variant="outline"
            onPress={onDeletePress}
            isPending={isDeletePending}
            isDisabled={isRemovePending}
            testId={deleteTestId}
          />
          <Button
            label="Cancel"
            variant="text"
            onPress={onClose}
            isDisabled={isRemovePending || isDeletePending}
            testId={cancelTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}
